import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Modal, Alert, Space, Button, Spin, Result, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import useRoomCard from '@/hooks/useRoomCard';
import useUploadDoorLock, { CardUsage, UploadStatus } from '@/hooks/useUploadDoorLock';
import { isNil } from '@/utils/number';
import * as CardApi from '@/api/card';

import { LockVendor } from './config';
import { CheckCardProps } from './interface';
import { transformParamsC2PMS } from './helper';
import styles from './index.module.scss';

const CheckCardModal: React.FC<CheckCardProps> = ({
  checkModalVisibile,
  roomIds,
  roomNo,
  enabledVendor,
  ...restProps
}) => {
  const [hotelId, setHotelId] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState(false);

  const {
    cardInstance,
    roomCardInfo,
    readError,
    reading,
    blankCard,
    resetCardStates,
    startRead,
    retryRead,
  } = useRoomCard({
    hotelId,
    vendorInfo: enabledVendor,
  });

  const { startUploadDoorInfo, endUploadDoorInfo } = useUploadDoorLock();

  // 获取酒店id(H21)
  const fetchHotelId = () => {
    return CardApi.fetchHotelId();
  };

  // 根据品牌区分退卡参数
  const generateCheckParams = () => {
    let result = null;

    // H21
    if (enabledVendor?.lockCode === LockVendor.H21) {
      result = {
        hotelid: hotelId,
      };
    }

    // 必达
    if (enabledVendor?.lockCode === LockVendor.BD) {
      result = {
        cardno: roomCardInfo.serialnumber,
        username: enabledVendor.dbRoot,
        password: enabledVendor.dbPass,
      };
    }

    // 杨格
    if (enabledVendor?.lockCode === LockVendor.YG) {
      result = {
        cardno: roomCardInfo.serialnumber,
      };
    }

    //  创佳
    if (enabledVendor?.lockCode === LockVendor.CHUANGJIA) {
      result = {
        lockno: roomCardInfo.lockno,
      };
    }

    return result;
  };

  // 退房卡
  const checkCard = () => {
    if (cardInstance !== null && cardInstance.connected) {
      setRequestLoading(true);

      const params = generateCheckParams();
      cardInstance
        .deleteRoomCard(params)
        .then(() => {
          message.success(intl.get('cpm_cards_return_success'));
          setRequestLoading(false);
          if (restProps.checkCallBack && typeof restProps.checkCallBack === 'function')
            restProps.checkCallBack();

          endUploadDoorInfo({ result: UploadStatus.Success });
        })
        .catch((e) => {
          // message.error(e.error || '退房卡失败');
          setRequestLoading(false);
          setRequestError(true);

          endUploadDoorInfo({ result: UploadStatus.Fail, remark: e.error });
        });
    }
  };

  const checkRoomCard = async (params) => {
    if (!params?.cardNo) {
      return;
    }
    setRequestLoading(true);
    try {
      const { status, msg } = await CardApi.checkRoomCard({
        roomIds,
        ...params,
      });
      if (status) {
        checkCard();
      } else {
        setRequestLoading(false);
        setRequestError(true);

        endUploadDoorInfo({ result: UploadStatus.Fail, remark: msg });
      }
    } catch (e) {
      setRequestLoading(false);
      setRequestError(true);

      endUploadDoorInfo({ result: UploadStatus.Fail, remark: e.msg });
    }
  };

  const retry = () => {
    startUploadDoorInfo({ type: CardUsage.Check, roomNo });

    setRequestLoading(false);
    setRequestError(false);
    retryRead();
  };

  const resetStates = () => {
    setRequestLoading(false);
    setRequestError(false);
    setHotelId(null);
    resetCardStates();
  };

  const closeModal = () => {
    resetStates();
    if (restProps.checkCallBack && typeof restProps.checkCallBack === 'function')
      restProps.checkCallBack();
  };

  useEffect(() => {
    if (checkModalVisibile) {
      startUploadDoorInfo({ type: CardUsage.Check, roomNo });

      // H21需要先获取酒店id
      if (enabledVendor?.lockCode === LockVendor.H21) {
        fetchHotelId().then((res) => {
          setHotelId(Number(res.data));
        });
      } else {
        startRead();
      }
    } else {
      resetStates();
    }
  }, [checkModalVisibile]);

  // 开始读卡(H21)
  useEffect(() => {
    if (!isNil(hotelId)) {
      startRead();
    }
  }, [hotelId]);

  // 成功读取到房卡
  useEffect(() => {
    if (enabledVendor?.lockCode) {
      const cardInfo = transformParamsC2PMS(enabledVendor?.lockCode, roomCardInfo);

      checkRoomCard({
        cardNo: cardInfo.serialnumber,
        hotelId: enabledVendor?.lockCode === LockVendor.H21 ? cardInfo.hotelid : undefined,
      });
    }
  }, [enabledVendor?.lockCode, roomCardInfo]);

  // reset states when unmounted
  useEffect(() => {
    return () => {
      resetStates();
    };
  }, []);

  const modalFooter = () => {
    if (requestError || readError || blankCard) {
      return (
        <Space align="start" size={16}>
          <Button type="primary" onClick={() => retry()}>
            {intl.get('app_confirm_retry_btn')}
          </Button>
          <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
        </Space>
      );
    }

    return <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>;
  };

  const renderHint = () => {
    if (requestError || readError || blankCard) {
      return (
        <Alert message={intl.get('cpm_cards_data_abnormal_canot_return')} type="error" showIcon />
      );
    }

    return <Alert message={intl.get('cpm_cards_put_on_write_mechine')} type="info" showIcon />;
  };

  const renderCardInfo = () => {
    if (requestError || readError || blankCard) {
      return (
        <Result
          style={{ height: 200 }}
          status="error"
          subTitle={intl.get('cpm_cards_info_abnormal')}
        />
      );
    }

    if (requestLoading || reading) {
      return (
        <div className={styles['read-card-loading']}>
          <Spin
            tip={intl.get('cpm_cards_info_reding')}
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      width={560}
      title={intl.get('cpm_cards_return')}
      visible={checkModalVisibile}
      maskClosable={false}
      keyboard={false}
      footer={modalFooter()}
      destroyOnClose
      onCancel={() => closeModal()}
    >
      {renderHint()}
      {renderCardInfo()}
    </Modal>
  );
};

const mapStateToProps = (state) => {
  const { roomCardEnabled, enabledVendor } = state.card;
  return { roomCardEnabled, enabledVendor };
};

export default connect(mapStateToProps, null)(CheckCardModal);
